import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import React from 'react';

import StatAlert from './StatAlert';

export type EventTimelineVariant =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'grey';

export interface EventTimelineItem {
  label: string;
  subtitle: string;
  valueLabel: string;
  date?: string;
  variant?: EventTimelineVariant;
  key?: string | number;
}

export interface EventTimelineProps {
  events: EventTimelineItem[];
  position?: 'left' | 'right' | 'alternate' | 'alternate-reverse';
  hideLastConnector?: boolean;
  size?: 'small' | 'medium';
}

const VARIANT_TO_DOT_COLOR: Record<
  EventTimelineVariant,
  'primary' | 'success' | 'error' | 'warning' | 'info' | 'grey'
> = {
  primary: 'primary',
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
  grey: 'grey',
};

const EventTimeline: React.FC<EventTimelineProps> = ({
  events,
  position = 'right',
  hideLastConnector = true,
  size = 'medium',
}) => {
  return (
    <Timeline
      position={position}
      sx={{
        paddingLeft: 0,
        paddingRight: 0,
        marginLeft: 0,
        marginRight: 0,
        '& .MuiTimelineItem-root': {
          minHeight: 'auto',
        },
        '& .MuiTimelineItem-root:before': {
          flex: 0,
          padding: 0,
        },
      }}
    >
      {events.map((event, index) => {
        const variant = event.variant ?? 'primary';
        const dotColor = VARIANT_TO_DOT_COLOR[variant];
        const isLast = index === events.length - 1;
        const itemKey = event.key ?? index;

        return (
          <TimelineItem key={itemKey}>
            <TimelineSeparator>
              <TimelineConnector
                sx={{
                  visibility:
                    hideLastConnector && isLast ? 'hidden' : 'visible',
                }}
              />
              <TimelineDot color={dotColor} variant="outlined" />
              <TimelineConnector
                sx={{
                  visibility:
                    hideLastConnector && isLast ? 'hidden' : 'visible',
                }}
              />
            </TimelineSeparator>
            <TimelineContent sx={{ py: 1, px: 2 }}>
              <StatAlert
                label={event.label}
                value={event.subtitle}
                valueLabel={event.valueLabel}
                color={dotColor}
                size={size}
              />
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};

export default EventTimeline;
